
import { config } from '@/config/env';
import type { GoogleCredentialResponse } from './types';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleIdConfiguration) => void;
          renderButton: (element: HTMLElement, config: GoogleButtonConfiguration) => void;
          prompt: () => void;
          disableAutoSelect: () => void;
        };
        oauth2: {
          initTokenClient: (config: TokenClientConfig) => TokenClient;
        };
      };
    };
  }
}

interface GoogleIdConfiguration {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  context?: string;
  ux_mode?: string;
  login_uri?: string;
  native_callback?: (response: any) => void;
  intermediate_iframe_close_callback?: () => void;
}

interface GoogleButtonConfiguration {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  locale?: string;
}

interface TokenClientConfig {
  client_id: string;
  scope: string;
  callback: (response: TokenResponse) => void;
  error_callback?: (error: any) => void;
}

interface TokenClient {
  requestAccessToken: () => void;
}

interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
}

class GoogleAuthService {
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;
  private isGoogleInitialized = false;

  async initialize(): Promise<void> {
    if (this.isInitialized) return;
    if (this.initializationPromise) return this.initializationPromise;

    this.initializationPromise = this.loadGoogleScript();
    await this.initializationPromise;
    this.isInitialized = true;
  }

  private loadGoogleScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        // Wait a bit for the Google API to be fully available
        setTimeout(() => {
          if (window.google?.accounts?.id) {
            resolve();
          } else {
            reject(new Error('Google Identity Services failed to load'));
          }
        }, 100);
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load Google Identity Services script'));
      };

      document.head.appendChild(script);
    });
  }

  initializeGoogleAuth(callback: (response: GoogleCredentialResponse) => void): void {
    if (!window.google?.accounts?.id) {
      throw new Error('Google Identity Services not loaded');
    }

    if (this.isGoogleInitialized) return;

    console.log('Initializing Google Auth with client ID:', config.google.clientId);
    console.log('Current origin:', window.location.origin);
    
    window.google.accounts.id.initialize({
      client_id: config.google.clientId,
      callback,
      auto_select: false,
      cancel_on_tap_outside: false,
    });

    this.isGoogleInitialized = true;
    console.log('Google Auth initialized successfully');
  }

  renderButton(
    element: HTMLElement,
    options: GoogleButtonConfiguration = {}
  ): void {
    if (!window.google?.accounts?.id) {
      throw new Error('Google Identity Services not loaded');
    }

    const defaultOptions: GoogleButtonConfiguration = {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
    };

    console.log('Rendering Google button with options:', { ...defaultOptions, ...options });

    window.google.accounts.id.renderButton(element, {
      ...defaultOptions,
      ...options,
    });
  }

  // New method for requesting Gmail permissions
  requestGmailPermissions(callback: (token: string) => void, errorCallback?: (error: any) => void): void {
    if (!window.google?.accounts?.oauth2) {
      throw new Error('Google OAuth2 not available');
    }

    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: config.google.clientId,
      scope: 'https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.metadata',
      callback: (response: TokenResponse) => {
        console.log('Gmail permissions granted:', response);
        callback(response.access_token);
      },
      error_callback: (error: any) => {
        console.error('Gmail permission error:', error);
        if (errorCallback) {
          errorCallback(error);
        }
      },
    });

    tokenClient.requestAccessToken();
  }

  disableAutoSelect(): void {
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
    }
  }
}

export const googleAuthService = new GoogleAuthService();
